import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoginBackground from "../../components/auth/LoginBackground";
import loginRequest from "../../api/LoginRequest";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await loginRequest.forgotPassword({ email });
      if (response.data?.data?.status_code === "000") {
        setUserData(response.data.data.data);
        setIsSubmitted(true);
        toast.success(
          response.data?.data?.message ||
            "Password reset code sent to your email!"
        );
       
      } else {
        console.error("Forgot Password Error:", response.data);
        toast.error(
          response.data?.data?.reason ||
            "Failed to send reset code. Please try again."
        ); //console.log("Forgot Password Response:", response);
        setError("Failed to send reset code. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to send reset code. Please try again.");
      setError("Failed to send reset code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetNavigation = () => {
    setIsSubmitted(false);
    navigate("/reset-password", { state: { userData: userData } });
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) setError("");
  };

  if (isSubmitted) {
    return (
      <div className="flex overflow-hidden relative justify-center items-center min-h-screen bg-gradient-to-br via-red-900 to-red-800 from-slate-900">
        <LoginBackground />

        <div className="relative z-10 px-6 mx-auto w-full max-w-md">
          <div className="p-8 rounded-2xl border shadow-2xl backdrop-blur-xl bg-white/10 border-white/20">
            <div className="mb-8 text-center">
              <div className="mb-6">
                <div className="inline-flex justify-center items-center w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-2xl shadow-lg">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <h1 className="mb-2 text-3xl font-bold text-transparent text-white bg-clip-text bg-gradient-to-r from-red-400 to-yellow-400">
                Check Your Email
              </h1>
              <p className="mb-6 text-sm text-gray-300">
                We've sent a password reset code to{" "}
                <span className="font-medium text-yellow-400">{email}</span>
              </p>

              <div className="p-4 rounded-xl border bg-white/5 border-white/10">
                <p className="text-xs text-gray-300">
                  Didn't receive the code? Check your spam folder or try again
                  with a different email address.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Proceed to reset password page */}
              <button
                onClick={handlePasswordResetNavigation}
                className="px-4 py-3 w-full text-sm font-medium text-white bg-gradient-to-r from-red-600 to-yellow-500 rounded-xl border border-transparent shadow-sm transition-all duration-200 transform hover:from-red-700 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:scale-105"
              >
                Proceed to Reset Password
              </button>
              <button
                onClick={() => setIsSubmitted(false)}
                className="px-4 py-3 w-full text-sm font-medium text-white bg-gradient-to-r from-red-600 to-yellow-500 rounded-xl border border-transparent shadow-sm transition-all duration-200 transform hover:from-red-700 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:scale-105"
              >
                Try Again
              </button>

              <Link
                to="/login"
                className="flex justify-center px-4 py-3 w-full text-sm font-medium text-gray-300 rounded-xl border transition-all duration-200 border-white/20 hover:bg-white/10 hover:text-white"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden relative justify-center items-center min-h-screen bg-gradient-to-br via-red-900 to-red-800 from-slate-900">
      <LoginBackground />

      <div className="relative z-10 px-6 mx-auto w-full max-w-md">
        <div className="p-8 rounded-2xl border shadow-2xl backdrop-blur-xl bg-white/10 border-white/20">
          <div className="mb-8 text-center">
            <div className="mb-6">
              <div className="inline-flex justify-center items-center w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-2xl shadow-lg transition-transform duration-300 transform hover:scale-105">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="mb-2 text-3xl font-bold text-gray-800 bg-clip-text  to-yellow-400">
              Forgot Password?
            </h1>
            <p className="text-sm text-gray-500">
              Enter your email address and we'll send you a code to reset your
              password
            </p>

            <div className="flex justify-center items-center mt-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-transparent via-white/30"></div>
              <div className="mx-4 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-transparent via-white/30"></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border bg-white/10 backdrop-blur-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  error ? "border-red-400" : "border-white/20"
                }`}
                placeholder="Enter your email address"
                disabled={isLoading}
              />
              {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex justify-center px-4 py-3 w-full text-sm font-medium text-white bg-gradient-to-r from-red-600 to-yellow-500 rounded-xl border border-transparent shadow-sm transition-all duration-200 transform hover:from-red-700 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </div>
              ) : (
                "Send Reset Link"
              )}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-gray-900"
              >
                ← Back to Login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
